import { cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './cn';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** Tooltip content shown on hover/focus. */
  content: React.ReactNode;
  /** Which side of the trigger to render on. Default 'top'. */
  side?: TooltipSide;
  /** The single element that triggers the tooltip. */
  children: React.ReactElement;
  /** Delay in ms before showing on hover/focus. Default 150. */
  delay?: number;
  /** Class applied to the tooltip panel. */
  className?: string;
}

/** Panel position relative to the inline-flex wrapper. */
const sidePosition: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

/** Entry/exit slide offset (4px) coming *from* the trigger toward the panel. */
const sideOffset: Record<TooltipSide, { x: number; y: number }> = {
  top: { x: 0, y: 4 },
  bottom: { x: 0, y: -4 },
  left: { x: 4, y: 0 },
  right: { x: -4, y: 0 },
};

/**
 * Lightweight hover/focus tooltip — replaces MUI <Tooltip />.
 * Dependency-free (CSS positioning, no popper). Shows on mouseenter/focus with a
 * delay, hides on leave/blur. Animated via motion + AnimatePresence.
 */
export function Tooltip({
  content,
  side = 'top',
  children,
  delay = 150,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  const tooltipId = useId();

  const clearTimer = useCallback(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const show = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [clearTimer, delay]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const offset = sideOffset[side];

  // Preserve any handlers already on the child trigger.
  const childProps = children.props as Record<string, unknown> & {
    onMouseEnter?: React.MouseEventHandler;
    onMouseLeave?: React.MouseEventHandler;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
  };

  const trigger = cloneElement(children, {
    'aria-describedby': open ? tooltipId : undefined,
    onMouseEnter: (e: React.MouseEvent) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: React.FocusEvent) => {
      childProps.onFocus?.(e);
      show();
    },
    onBlur: (e: React.FocusEvent) => {
      childProps.onBlur?.(e);
      hide();
    },
  } as Partial<React.HTMLAttributes<HTMLElement>>);

  return (
    <span className="relative inline-flex" onMouseLeave={hide}>
      {trigger}
      <AnimatePresence>
        {open && content != null && content !== '' && (
          <motion.span
            id={tooltipId}
            role="tooltip"
            initial={{ opacity: 0, x: offset.x, y: offset.y }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: offset.x, y: offset.y }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'pointer-events-none absolute z-[1400] whitespace-nowrap rounded-lg border border-line',
              'bg-surface-2 px-2.5 py-1.5 text-xs font-medium text-content shadow-pop',
              sidePosition[side],
              className,
            )}
          >
            {content}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
